import classNames from 'classnames';

import { useBlobUrl, useIsDesktop } from '../../hooks';
import { Button } from '../../Button';
import { Icon } from '../../Icon';

import { PreviewButtons } from './Buttons';
import { useStyles } from './Preview.styles';

/**
 * Свойства компонента {@link PreviewImage}
 */
interface PreviewImageProps {
  /**
   * URL изображения для предварительного просмотра.
   */
  url: string;

  /**
   * Форма отображения изображения ('auto' или 'square').
   */
  shape: 'auto' | 'square';

  /**
   * Определяет, является ли изображение кликабельным.
   */
  clickable: boolean;

  /**
   * Функция для загрузки изображения.
   */
  onClickDownload?: () => Promise<
    Blob | { file: Promise<Blob>; filename?: string }
  >;

  /**
   * Определяет, нужно ли отображать сворачиваемый нижний колонтитул.
   */
  withCollapsableFooter?: boolean;

  /**
   * Определяет, нужно ли центрировать изображение.
   */
  centerImage?: boolean;

  /**
   * Коллбэк, вызываемый после успешной загрузки изображения.
   */
  onLoadSuccess?: () => void;

  /**
   * Функция для загрузки полной версии изображения.
   */
  onLoadFull?: () => Promise<Blob>;

  /**
   * Коллбэк, вызываемый для раскрытия превью изображения.
   */
  onExpand?: () => void;

  /**
   * Высота изображения.
   */
  height?: string | number;

  /**
   * Ширина изображения.
   */
  width?: string | number;

  /**
   * Максимальная высота изображения.
   */
  maxHeight?: string | number;

  /**
   * Максимальная ширина изображения.
   */
  maxWidth?: string | number;
}

/**
 * Компонент для предварительного просмотра изображения.
 *
 * @internal
 * @param url - URL изображения для предварительного просмотра.
 * @param shape - Форма отображения изображения ('auto' или 'square').
 * @param clickable - Определяет, является ли изображение кликабельным.
 * @param onClickDownload - Функция для загрузки изображения.
 * @param withCollapsableFooter - Определяет, нужно ли отображать сворачиваемый нижний колонтитул.
 * @param centerImage - Определяет, нужно ли центрировать изображение.
 * @param onLoadSuccess - Коллбэк, вызываемый после успешной загрузки изображения.
 * @param onExpand - Коллбэк, вызываемый для раскрытия превью изображения.
 * @param onLoadFull - Функция для загрузки полной версии изображения.
 * @param height - Высота изображения.
 * @param maxHeight - Максимальная высота изображения.
 * @param maxWidth - Максимальная ширина изображения.
 * @param width - Ширина изображения.
 * @returns  Элемент React.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link PreviewImageProps}
 */
export const PreviewImage = ({
  url,
  shape,
  clickable,
  onClickDownload,
  withCollapsableFooter,
  centerImage,
  onLoadSuccess,
  onExpand,
  onLoadFull,
  height,
  width,
  maxHeight,
  maxWidth,
}: PreviewImageProps) => {
  const { classes } = useStyles({ height, width, maxHeight, maxWidth });

  const isDesktop = useIsDesktop();
  const { createUrl } = useBlobUrl();

  const handleImageLoad = () => {
    if (onLoadSuccess) {
      onLoadSuccess();
    }
  };

  const imageClasses = classNames(
    classes.image,
    shape === 'square' && classes.image_square,
  );

  return (
    <div
      className={classNames(
        classes.root,
        classes.collapseWrapper,
        centerImage && classes.centerImage,
        'aqa_file_preview_image',
      )}
    >
      {shape === 'square' && <div className={classes.square} />}
      {clickable ? (
        <a
          href={url}
          onClick={(e) => {
            if (onLoadFull) {
              e.preventDefault();
              e.stopPropagation();

              onLoadFull()
                .then((blob) => createUrl(blob))
                .catch(() => url)
                .then((objUrl) => {
                  window.open(objUrl, '_blank');
                });
            }
          }}
          target="_blank"
          rel="noreferrer"
        >
          <img
            className={imageClasses}
            src={url}
            alt=""
            onLoad={handleImageLoad}
          />
        </a>
      ) : (
        <img
          className={imageClasses}
          src={url}
          alt=""
          onLoad={handleImageLoad}
        />
      )}
      {onClickDownload && (
        <PreviewButtons
          withCollapsableFooter={withCollapsableFooter}
          onClickDownload={onClickDownload}
        />
      )}
      {onExpand && isDesktop && (
        <div className={classes.expandButton}>
          <Button
            icon={<Icon name="fullscreen_outline" size="20" />}
            size="small"
            onClick={onExpand}
          />
        </div>
      )}
    </div>
  );
};
